<template>
  <div class="flex flex-col gap-4">
    <div
      :class="[table(), 'grid-cols-[auto_max-content_max-content_max-content_max-content_160px]']"
    >
      <div :class="tableHeader()">
        <div class="col-start-1">{{ t('lane') }}</div>
        <div>{{ t('counterpartyType.label') }}</div>
        <div>{{ t('taxProcedure') }}</div>
        <div :class="tableCell({ numeric: true })">{{ t('taxPercent') }}</div>
        <div :class="tableCell({ numeric: true })">{{ t('balance') }}</div>
        <div :class="tableCell({ numeric: true })">{{ t('creditAmount') }}</div>
      </div>
      <template v-if="creditBuckets.length">
        <div
          v-for="bucket in creditBuckets"
          :key="bucket.id"
          :class="tableRow()"
          :data-testid="`manual-lane-credit-row-${bucket.id}`"
        >
          <div class="font-medium">{{ bucket.label }}</div>
          <CounterpartyTypeBadge :counterparty-type="bucket.counterpartyType" size="sm" />
          <div>{{ t(`taxProcedure.${bucket.taxProcedure}`) }}</div>
          <div :class="tableCell({ numeric: true })">
            {{ formatPercent(bucket.taxPercent / 100, { maximumFractionDigits: 2 }) }}
          </div>
          <div :class="tableCell({ numeric: true, class: 'font-bold text-green-700' })">
            {{ formatCurrency(bucket.balanceCents / 100) }}
          </div>
          <div :class="tableCell({ numeric: true })" data-testid="manual-lane-credit-amount-input">
            <Input
              :model-value="creditAmounts[bucket.id] ?? 0"
              type="number"
              :min-fraction-digits="2"
              :max-fraction-digits="2"
              :min="bucket.excludedCents / 100"
              :max="bucket.balanceCents / 100"
              :step="0.01"
              adornment="€"
              class="w-full"
              @update:model-value="(value) => updateCreditAmount(bucket.id, value)"
            />
          </div>
        </div>
        <div v-if="totalCreditAmount !== 0" :class="tableFooter({ muted: true })">
          <div class="col-span-5">{{ t('totalCredit') }}</div>
          <div :class="tableCell({ numeric: true, class: 'font-bold' })">
            {{ formatCurrency(totalCreditAmount) }}
          </div>
        </div>
      </template>
      <EntityListExceptions
        :status="status"
        :error="error"
        :items-count="creditBuckets.length"
        :no-items-text="t('noCreditBuckets')"
        class="col-span-full"
      />
    </div>
    <TextareaField
      name="comment"
      :label="t('comment')"
      :placeholder="''"
      :rows="4"
      prevent-error-on-mount
      class="w-full"
    />
    <div class="flex items-center justify-end gap-4">
      <CVButton variant="default" :disabled="!canSubmit || isPending" @click.prevent="handleCredit">
        {{ t('creditButton') }}
      </CVButton>
      <CVButton variant="secondary" @click.prevent="emit('cancel')">{{ t('cancel') }}</CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import CounterpartyTypeBadge from '@/components/badges/CounterpartyTypeBadge.vue';
import EntityListExceptions from '@/components/EntityListExceptions.vue';
import Input from '@/components/Input.vue';
import TextareaField from '@/components/TextareaField.vue';
import type { Booking } from '@/entities/bookings/booking.entity';
import type { LedgerEntry } from '@/entities/ledger/ledger-entry.entity';
import { CounterpartyType } from '@/entities/ledger/ledger-entry.entity';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { getTranslatedLedgerEntryLane } from '@/utils/get-translated-ledger-entry-lane';
import { useManualLaneCredit } from '@/queries/use-bookings';
import { useLedgerEntries } from '@/queries/use-ledger';
import { Alert } from '@/services/alert.service';
import { formatCurrency, formatPercent } from '@/utils/format-numbers';
import { isManualLaneCreditable } from '@/utils/is-manual-lane-creditable';
import { manualLaneCreditCommentSchema } from '@/validation/manual-lane-credit.schema';
import { table, tableCell, tableFooter, tableHeader, tableRow } from '@carvia/theme/lib/cva/grid';
import { FindAllLedgerEntriesSort, Order, TaxProcedure } from '@carvia/ros-client-types';
import { useForm } from 'vee-validate';
import { computed, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  booking: Booking;
}>();

const emit = defineEmits<{
  (e: 'cancel'): void;
  (e: 'success'): void;
}>();

const { t } = useI18n();

type CreditBucket = {
  id: string;
  lane: string;
  counterpartyType: CounterpartyType;
  taxProcedure: TaxProcedure;
  taxPercent: number;
  balanceCents: number;
  excludedCents: number;
  label: string;
};

function buildCreditBucketId(
  counterpartyType: CounterpartyType,
  taxProcedure: TaxProcedure,
  taxPercent: number,
  lane: string,
) {
  return `${counterpartyType}:${taxProcedure}:${taxPercent}:${lane}`;
}

function buildCreditBuckets(entries: LedgerEntry[]): CreditBucket[] {
  const buckets = new Map<string, CreditBucket>();

  for (const entry of entries) {
    if (!isManualLaneCreditable(entry.lane)) {
      continue;
    }

    const id = buildCreditBucketId(
      entry.counterpartyType,
      entry.taxProcedure,
      entry.taxPercent,
      entry.lane,
    );
    const existing = buckets.get(id);
    const balanceCents = (existing?.balanceCents ?? 0) + entry.amount;
    const excludedCents =
      (existing?.excludedCents ?? 0) + (entry.excludedFromReconciliation ? entry.amount : 0);

    buckets.set(id, {
      id,
      lane: entry.lane,
      counterpartyType: entry.counterpartyType,
      taxProcedure: entry.taxProcedure,
      taxPercent: entry.taxPercent,
      balanceCents,
      excludedCents,
      label: getTranslatedLedgerEntryLane(entry.lane, entry.description),
    });
  }

  return [...buckets.values()]
    .filter((bucket) => bucket.balanceCents > 0 || bucket.excludedCents < 0)
    .sort((a, b) => a.id.localeCompare(b.id));
}

const {
  data: ledgerEntriesData,
  status,
  error,
} = useLedgerEntries(
  computed(() => ({
    bookingIds: [props.booking.id],
    sort: [FindAllLedgerEntriesSort.DUE_DATE],
    order: Order.ASC,
    limit: 999,
  })),
);

const ledgerEntries = useFlattenPaginatedData(ledgerEntriesData);

const creditBuckets = computed(() => buildCreditBuckets(ledgerEntries.value));

const creditAmounts = reactive<Record<string, number>>({});

watch(
  creditBuckets,
  (buckets) => {
    const bucketIds = new Set(buckets.map((bucket) => bucket.id));
    for (const bucket of buckets) {
      if (creditAmounts[bucket.id] === undefined) {
        creditAmounts[bucket.id] = 0;
      }
    }
    for (const bucketId of Object.keys(creditAmounts)) {
      if (!bucketIds.has(bucketId)) {
        delete creditAmounts[bucketId];
      }
    }
  },
  { immediate: true },
);

const { handleSubmit, meta } = useForm({
  initialValues: {
    comment: '',
  },
  validationSchema: manualLaneCreditCommentSchema,
});

const selectedCredits = computed(() =>
  creditBuckets.value
    .map((bucket) => ({
      bucket,
      amount: creditAmounts[bucket.id] ?? 0,
    }))
    .filter(({ amount }) => amount !== 0),
);

const totalCreditAmount = computed(() =>
  selectedCredits.value.reduce((sum, { amount }) => sum + amount, 0),
);

const hasValidCredits = computed(() =>
  selectedCredits.value.every(
    ({ bucket, amount }) =>
      amount !== 0 && amount <= bucket.balanceCents / 100 && amount >= bucket.excludedCents / 100,
  ),
);

const canSubmit = computed(
  () => selectedCredits.value.length > 0 && hasValidCredits.value && meta.value.valid,
);

const { mutateAsync: createManualLaneCredit, isPending } = useManualLaneCredit(props.booking.id);

const updateCreditAmount = (bucketId: string, value: string | number | null | undefined) => {
  const amount = value === undefined || value === null || value === '' ? 0 : Number(value);
  creditAmounts[bucketId] = Number.isNaN(amount) ? 0 : amount;
};

const handleCredit = handleSubmit(async ({ comment }) => {
  if (!canSubmit.value) {
    return;
  }

  const { isConfirmed } = await Alert.fire({
    titleText: t('confirmTitle'),
    icon: 'warning',
    showCancelButton: true,
    cancelButtonText: t('cancel'),
    confirmButtonText: t('confirmButton'),
  });
  if (!isConfirmed) {
    return;
  }

  await createManualLaneCredit({
    comment,
    credits: selectedCredits.value.map(({ bucket, amount }) => ({
      lane: bucket.lane,
      counterpartyType: bucket.counterpartyType,
      taxProcedure: bucket.taxProcedure,
      taxPercent: bucket.taxPercent,
      amount,
    })),
  });

  emit('success');
});
</script>

<i18n lang="json">
{
  "en": {
    "lane": "Charge",
    "taxProcedure": "Tax procedure",
    "taxProcedure.REGULAR": "Regular",
    "taxProcedure.REVERSE_CHARGE": "Reverse charge",
    "taxProcedure.EXPORT": "Export",
    "taxPercent": "Tax rate",
    "balance": "Balance",
    "creditAmount": "Credit",
    "totalCredit": "Total credit",
    "noCreditBuckets": "No creditable charges found",
    "comment": "Reason for credit",
    "creditButton": "Credit charge",
    "confirmTitle": "Really credit this charge?",
    "confirmButton": "Credit"
  },
  "de": {
    "lane": "Posten",
    "taxProcedure": "Steuerverfahren",
    "taxProcedure.REGULAR": "Regulär",
    "taxProcedure.REVERSE_CHARGE": "Reverse Charge",
    "taxProcedure.EXPORT": "Export",
    "taxPercent": "Steuersatz",
    "balance": "Saldo",
    "creditAmount": "Gutschrift",
    "totalCredit": "Gutschrift gesamt",
    "noCreditBuckets": "Keine gutschreibbaren Posten gefunden",
    "comment": "Grund für die Gutschrift",
    "creditButton": "Posten gutschreiben",
    "confirmTitle": "Wirklich diesen Posten gutschreiben?",
    "confirmButton": "Gutschreiben"
  },
  "es": {
    "lane": "Concepto",
    "taxProcedure": "Procedimiento fiscal",
    "taxProcedure.REGULAR": "Regular",
    "taxProcedure.REVERSE_CHARGE": "Inversión del sujeto pasivo",
    "taxProcedure.EXPORT": "Exportación",
    "taxPercent": "Tasa de impuestos",
    "balance": "Saldo",
    "creditAmount": "Abono",
    "totalCredit": "Abono total",
    "noCreditBuckets": "No hay partidas abonables",
    "comment": "Motivo del abono",
    "creditButton": "Abonar partida contable",
    "confirmTitle": "¿Seguro que desea abonar esta partida contable?",
    "confirmButton": "Abonar"
  }
}
</i18n>
